/**
 * <s-photo-lightbox>
 * Wraps an editorial photo gallery. Every button with [data-lightbox-open] inside it
 * opens the shared <dialog> showing that button's image at full size, with the
 * caption taken from the nearest <figure>'s <figcaption>. Arrow keys and the
 * prev/next buttons move through the gallery; Escape and the close button dismiss it.
 */
class PhotoLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.media = this.dialog.querySelector('[data-lightbox-image]');
    this.caption = this.dialog.querySelector('[data-lightbox-caption]');
    this.index = 0;

    this.onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const trigger = target.closest('[data-lightbox-open]');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        this.open(this.triggers().indexOf(trigger));
        return;
      }
      if (target.closest('[data-lightbox-prev]')) this.step(-1);
      else if (target.closest('[data-lightbox-next]')) this.step(1);
      else if (target.closest('[data-lightbox-close]') || target === this.dialog) this.dialog.close();
    };

    this.onKeydown = (event) => {
      if (!this.dialog.open) return;
      const rtl = document.dir === 'rtl';
      if (event.key === 'ArrowRight') this.step(rtl ? -1 : 1);
      if (event.key === 'ArrowLeft') this.step(rtl ? 1 : -1);
    };

    this.onClose = () => {
      const trigger = this.triggers()[this.index];
      if (trigger) trigger.focus();
    };

    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('close', this.onClose);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.dialog) {
      this.dialog.removeEventListener('keydown', this.onKeydown);
      this.dialog.removeEventListener('close', this.onClose);
    }
  }

  triggers() {
    return Array.from(this.querySelectorAll('[data-lightbox-open]')).filter((el) => el.querySelector('img'));
  }

  largestSource(img) {
    const srcset = img.getAttribute('srcset');
    if (!srcset) return img.currentSrc || img.src;
    const candidates = srcset
      .split(',')
      .map((entry) => entry.trim().split(/\s+/))
      .map(([url, width]) => ({ url, width: parseInt(width, 10) || 0 }))
      .sort((a, b) => b.width - a.width);
    return candidates.length ? candidates[0].url : img.currentSrc || img.src;
  }

  open(index) {
    const triggers = this.triggers();
    if (!triggers.length || index < 0) return;
    this.index = (index + triggers.length) % triggers.length;
    const trigger = triggers[this.index];
    const img = trigger.querySelector('img');
    if (!img || !this.media) return;

    this.media.src = this.largestSource(img);
    this.media.alt = img.alt || '';

    if (this.caption) {
      const figcaption = trigger.closest('figure')?.querySelector('figcaption');
      const captionCopy = figcaption ? figcaption.cloneNode(true) : null;
      captionCopy?.querySelectorAll('a').forEach((link) => link.remove());
      this.caption.textContent = captionCopy ? captionCopy.textContent.replace(/\s+/g, ' ').trim() : '';
    }

    if (!this.dialog.open) this.dialog.showModal();
  }

  step(direction) {
    this.open(this.index + direction);
  }
}

if (!customElements.get('s-photo-lightbox')) {
  customElements.define('s-photo-lightbox', PhotoLightbox);
}
